import { Card, CardBody, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react';
import NextLink from 'next/link';
import type { ReactNode } from 'react';

import { type ComparisonLink, getCompetitorLinks } from '../../data/competitor-comparisons';
import {
  type CompetitorPricing,
  competitorPricingPath,
  describePlanPrice,
  describeStartingPrice,
  formatPricingAmount,
  getAnnualMonthlyCostForSeats,
  getMonthlyCostForSeats,
} from '../../data/competitor-pricing';
import {
  BILLING_PERIOD,
  EXPLORATION_MODE,
  getKreatliMonthlyCost,
  getKreatliPlan,
  KREATLI_PLANS,
  STORAGE_ADD_ON,
  TRIAL_DAYS,
} from '../../data/kreatli-plans';
import type { PseoFaq, SourceRef } from '../../data/pseo/types';
import { useSession } from '../../hooks/useSession';
import { SectionStoryblok } from '../../typings/storyblok';
import { getHeadingId } from '../../utils/storyblok';
import { Section } from '../layout/Storyblok/Section/Section';
import wysiwygStyles from '../layout/Storyblok/Wysiwyg/Wysiwyg.module.scss';
import { formatIsoDate, LastVerifiedBadge } from '../pseo/LastVerifiedBadge';
import { CTASection } from '../shared/CTASection';
import { FAQStructuredData } from '../shared/FAQStructuredData';
import { Icon } from '../various/Icon';
import { ComparisonPageLayout } from './ComparisonPageLayout';

/** Team sizes priced on every competitor pricing page. */
export const PRICING_SEAT_COUNTS = [3, 5, 10, 15] as const;

const TABLE_CARD_CLASS = 'border border-foreground-200 bg-content1/60 backdrop-blur-lg';

const fmtUsd = (amount: number) => formatPricingAmount(amount, 'USD');

export const CTA_DESCRIPTION = [
  `Explore Kreatli free (${EXPLORATION_MODE.label}), share a guest review link and collect timecoded `,
  `comments.`,
].join('');

export function makeHeroBlok(input: {
  id: string;
  title: string;
  text: string;
  coverSrc: string;
  coverAlt: string;
}): SectionStoryblok {
  return {
    _uid: `static-${input.id}-hero`,
    component: 'section',
    title: input.title,
    titleTag: 'h1',
    text: input.text,
    orientation: '',
    displaySocials: false,
    imageObjectFit: 'cover',
    image: {
      _uid: `static-${input.id}-cover`,
      id: null,
      alt: input.coverAlt,
      name: '',
      focus: '',
      source: '',
      title: input.title,
      filename: input.coverSrc,
      copyright: '',
      fieldtype: 'asset',
    },
  };
}

export function SummaryBox({ title = 'Article summary', children }: { title?: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-transparent p-6 backdrop-blur-lg sm:p-8">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex-shrink-0 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 p-2">
          <Icon icon="checkCircle" size={18} className="text-primary" />
        </div>
        <p className="font-sans text-base font-bold">{title}</p>
      </div>
      <div className="pl-1 text-sm leading-relaxed text-foreground-600 sm:text-base">{children}</div>
    </div>
  );
}

/** Every listed plan with price, seats, storage, reviewers and notes, as published by the vendor. */
export function PricingPlanTable({ entry }: { entry: CompetitorPricing }) {
  return (
    <Card className={TABLE_CARD_CLASS}>
      <CardBody className="overflow-x-auto p-2 sm:p-4">
        <Table aria-label={`${entry.vendor} plans and prices`} removeWrapper className="min-w-[720px]">
          <TableHeader>
            <TableColumn>Plan</TableColumn>
            <TableColumn>Price</TableColumn>
            <TableColumn>Seats</TableColumn>
            <TableColumn>Storage</TableColumn>
            <TableColumn>Reviewers</TableColumn>
            <TableColumn>Notes</TableColumn>
          </TableHeader>
          <TableBody>
            {entry.plans.map((plan) => (
              <TableRow key={plan.name}>
                <TableCell className="font-semibold text-foreground-700">{plan.name}</TableCell>
                <TableCell className="font-semibold">{describePlanPrice(plan, entry.currency)}</TableCell>
                <TableCell>{plan.seats}</TableCell>
                <TableCell>{plan.storage}</TableCell>
                <TableCell>{plan.reviewers}</TableCell>
                <TableCell className="text-sm text-foreground-500">{plan.notes ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

export interface SeatCostRow {
  seats: number;
  plan: string;
  monthly: string;
  annual: string;
  note: string;
}

export function getSeatCostRows(entry: CompetitorPricing, seatCounts: readonly number[]): SeatCostRow[] {
  return seatCounts.map((seats) => {
    const cost = getMonthlyCostForSeats(entry, seats, { requireVideo: true });
    const annual = getAnnualMonthlyCostForSeats(entry, seats, cost);
    let annualLabel = 'Not listed';
    if (cost.billing === 'annual') {
      annualLabel = 'Only the billed-annually price is listed';
    } else if (annual !== null) {
      annualLabel = formatPricingAmount(annual, entry.currency);
    }
    return {
      seats,
      plan: cost.planName ?? '—',
      monthly: cost.total === null ? 'Not listed' : formatPricingAmount(cost.total, cost.currency),
      annual: cost.total === null ? '—' : annualLabel,
      note: cost.note,
    };
  });
}

/** "What you'll actually pay" for each team size, computed from the registry with the plan rules. */
export function SeatCostTable({
  entry,
  seatCounts = PRICING_SEAT_COUNTS,
}: {
  entry: CompetitorPricing;
  seatCounts?: readonly number[];
}) {
  const rows = getSeatCostRows(entry, seatCounts);
  return (
    <Card className={TABLE_CARD_CLASS}>
      <CardBody className="overflow-x-auto p-2 sm:p-4">
        <Table aria-label={`${entry.vendor} monthly cost by team size`} removeWrapper className="min-w-[720px]">
          <TableHeader>
            <TableColumn>Seats</TableColumn>
            <TableColumn>Cheapest plan that fits</TableColumn>
            <TableColumn>Per month</TableColumn>
            <TableColumn>Per month, billed annually</TableColumn>
            <TableColumn>How it&apos;s calculated</TableColumn>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.seats}>
                <TableCell className="font-semibold text-foreground-700">{row.seats}</TableCell>
                <TableCell>{row.plan}</TableCell>
                <TableCell className="font-semibold">{row.monthly}</TableCell>
                <TableCell>{row.annual}</TableCell>
                <TableCell className="text-sm text-foreground-500">{row.note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

function kreatliCostLabel(seats: number): { plan: string; monthly: string; note: string } {
  const { plan, total } = getKreatliMonthlyCost(seats);
  if (total === null) {
    return {
      plan: plan.name,
      monthly: 'Custom',
      note: `Listed plans go up to ${getKreatliPlan('team').memberLimit} members; larger teams are on Enterprise.`,
    };
  }
  return { plan: plan.name, monthly: fmtUsd(total), note: `${seats} × ${fmtUsd(plan.priceMonthly ?? 0)} per seat.` };
}

/** Same team sizes on Kreatli, next to the competitor's monthly cost. */
export function KreatliCostTable({
  entry,
  seatCounts = PRICING_SEAT_COUNTS,
}: {
  entry: CompetitorPricing;
  seatCounts?: readonly number[];
}) {
  const competitorRows = getSeatCostRows(entry, seatCounts);
  return (
    <Card className={TABLE_CARD_CLASS}>
      <CardBody className="overflow-x-auto p-2 sm:p-4">
        <Table aria-label={`${entry.vendor} and Kreatli monthly cost by team size`} removeWrapper className="min-w-[640px]">
          <TableHeader>
            <TableColumn>Seats</TableColumn>
            <TableColumn>{`${entry.vendor} per month`}</TableColumn>
            <TableColumn>Kreatli plan</TableColumn>
            <TableColumn>Kreatli per month</TableColumn>
            <TableColumn>Kreatli calculation</TableColumn>
          </TableHeader>
          <TableBody>
            {competitorRows.map((row) => {
              const kreatli = kreatliCostLabel(row.seats);
              return (
                <TableRow key={row.seats}>
                  <TableCell className="font-semibold text-foreground-700">{row.seats}</TableCell>
                  <TableCell>{row.plan === '—' ? row.monthly : `${row.monthly} (${row.plan})`}</TableCell>
                  <TableCell>{kreatli.plan}</TableCell>
                  <TableCell className="font-semibold">{kreatli.monthly}</TableCell>
                  <TableCell className="text-sm text-foreground-500">{kreatli.note}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

/** Kreatli plan facts from `kreatli-plans.ts` (Exploration + paid plans). */
export function KreatliPlanList() {
  return (
    <ul>
      <li>
        <strong>{EXPLORATION_MODE.name} (free):</strong> {EXPLORATION_MODE.label}.
      </li>
      {KREATLI_PLANS.map((plan) => (
        <li key={plan.id}>
          <strong>{plan.name}:</strong>{' '}
          {plan.priceMonthly === null
            ? 'custom pricing, custom members and storage.'
            : [
                `${fmtUsd(plan.priceMonthly)} per user per month, up to ${plan.memberLimit} members, `,
                `${plan.storageLabel} storage.`,
              ].join('')}
        </li>
      ))}
      <li>
        Billed {BILLING_PERIOD}, with a {TRIAL_DAYS}-day trial on paid plans. Extra storage is {STORAGE_ADD_ON.label}.
        Guest review links are included on every plan.
      </li>
    </ul>
  );
}

export function ComparisonLinkList({ links }: { links: ComparisonLink[] }) {
  return (
    <ul>
      {links.map((link) => (
        <li key={link.href}>
          <NextLink href={link.href}>{link.label}</NextLink>
        </li>
      ))}
    </ul>
  );
}

export function SourceList({ sources }: { sources: SourceRef[] }) {
  return (
    <ul>
      {sources.map((source) => (
        <li key={source.url}>
          <a href={source.url} target="_blank" rel="noopener noreferrer">
            {source.label}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function FaqList({ faqs }: { faqs: PseoFaq[] }) {
  return (
    <div className="flex flex-col gap-5">
      {faqs.map((faq) => (
        <div key={faq.question} className="space-y-1.5">
          <h3 className="font-sans text-lg font-bold sm:text-xl">{faq.question}</h3>
          <p className="text-lg leading-relaxed text-foreground-500">{faq.answer}</p>
        </div>
      ))}
    </div>
  );
}

interface Props {
  entry: CompetitorPricing;
  /** `<title>` before " | Kreatli" (≤ 50 characters). */
  documentTitle: string;
  metaDescription: string;
  h1: string;
  heroText: string;
  publishDate: string;
  readTimeMinutes: string;
  coverSrc: string;
  coverAlt: string;
  /** Plain-language summary shown under the hero. */
  summary: string;
  /** Fair reasons to pick the competitor over Kreatli. */
  whenCompetitorIsBetter: string[];
}

/**
 * Shared template for `/comparisons/<vendor>-pricing`. Every number comes from the registry entry; seat costs are
 * computed with `getMonthlyCostForSeats`, Kreatli costs with `getKreatliMonthlyCost`. Only render publishable entries.
 */
export function CompetitorPricingPage({
  entry,
  documentTitle,
  metaDescription,
  h1,
  heroText,
  publishDate,
  readTimeMinutes,
  coverSrc,
  coverAlt,
  summary,
  whenCompetitorIsBetter,
}: Props) {
  useSession();

  const canonicalPath = competitorPricingPath(entry);
  const vendor = entry.vendor;
  const sections = [
    `${vendor} plans and prices`,
    `What you'll actually pay for ${PRICING_SEAT_COUNTS.slice(0, -1).join(', ')} and ${PRICING_SEAT_COUNTS[PRICING_SEAT_COUNTS.length - 1]} seats`,
    `${vendor} limits and gotchas`,
    `Kreatli equivalent cost`,
    `When ${vendor} is the better choice`,
    `Related pricing and comparisons`,
    'Sources',
    `FAQ: ${vendor} pricing`,
  ];
  const tocLinks = sections.map((label) => ({ label, url: `#${getHeadingId(label)}` }));
  const links = getCompetitorLinks([entry.id], canonicalPath);
  const currencyNote =
    entry.currency === 'USD'
      ? null
      : `${vendor} prices are in ${entry.currency} and are not converted. Kreatli prices are in USD.`;

  return (
    <>
      <FAQStructuredData faqs={entry.faqs} />
      <ComparisonPageLayout
        documentTitle={documentTitle}
        metaDescription={metaDescription}
        canonicalPath={canonicalPath}
        ogImageUrl={coverSrc}
        ogImageAlt={coverAlt}
        publishDate={publishDate}
        readTimeMinutes={readTimeMinutes}
        breadcrumbLabel={`${vendor} pricing`}
        tocLinks={tocLinks}
      >
        <Section blok={makeHeroBlok({ id: entry.slug, title: h1, text: heroText, coverSrc, coverAlt })} />

        <SummaryBox>
          <p>{summary}</p>
          <p className="mt-2">
            Starting price: <strong>{describeStartingPrice(entry)}</strong>. Prices checked{' '}
            {formatIsoDate(entry.lastVerified)} on {vendor}&apos;s pricing page.
          </p>
        </SummaryBox>

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(sections[0])}>{sections[0]}</h2>
          <p>
            List prices before tax, as {vendor} publishes them.{' '}
            {entry.freePlan ? entry.freePlan : `${vendor} lists no free plan.`}{' '}
            {entry.trial ? `Trial: ${entry.trial}` : null}
          </p>
        </div>

        <PricingPlanTable entry={entry} />

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(sections[1])}>{sections[1]}</h2>
          <p>
            The cheapest listed paid plan that fits each team size, per month before tax. Free plans are left out
            because
            of their project and file limits. When a cost can&apos;t be worked out from published prices, the table says
            why instead of guessing.
          </p>
        </div>

        <SeatCostTable entry={entry} />

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(sections[2])}>{sections[2]}</h2>
          <ul>
            {entry.gotchas.map((gotcha) => (
              <li key={gotcha}>{gotcha}</li>
            ))}
          </ul>

          <hr />

          <h2 id={getHeadingId(sections[3])}>{sections[3]}</h2>
          <p>
            Kreatli is priced per seat in USD. Here is what the same team sizes pay on Kreatli&apos;s cheapest plan that
            fits.{currencyNote ? ` ${currencyNote}` : null}
          </p>
        </div>

        <KreatliCostTable entry={entry} />

        <div className={wysiwygStyles.wysiwyg}>
          <p>Kreatli plans:</p>
          <KreatliPlanList />

          <hr />

          <h2 id={getHeadingId(sections[4])}>{sections[4]}</h2>
          <p>Kreatli is our product, so here is when {vendor} is the better pick:</p>
          <ul>
            {whenCompetitorIsBetter.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          <p>
            Kreatli also has no comment export, no editing-software panels, no website proofing and no frame-by-frame
            stepping. If those don&apos;t matter, compare it with{' '}
            <NextLink href="/platform/review-approval">Kreatli&apos;s video review software</NextLink> on a real client
            round.
          </p>

          <hr />

          <h2 id={getHeadingId(sections[5])}>{sections[5]}</h2>
          <ComparisonLinkList links={links} />

          <hr />

          <h2 id={getHeadingId(sections[6])}>{sections[6]}</h2>
        </div>

        <LastVerifiedBadge
          lastVerified={entry.lastVerified}
          sources={entry.sourceUrls}
          changelog={entry.changelog}
          notes={currencyNote ? [currencyNote] : []}
        />

        <div className={wysiwygStyles.wysiwyg}>
          <h2 id={getHeadingId(sections[7])}>{sections[7]}</h2>
        </div>

        <FaqList faqs={entry.faqs} />

        <CTASection
          title={`Compare ${vendor} with Kreatli on a real project`}
          description={CTA_DESCRIPTION}
          primaryButtonText="Start for Free"
          primaryButtonHref="/sign-up"
        />
      </ComparisonPageLayout>
    </>
  );
}
